import { CustomErrorMessage, useField } from '@lingoda/forms';
import { FormControl } from '@lingoda/ui';
import { OptionCard } from './OptionCard';
import { OptionList } from './OptionList';
import type { ChangeEvent, ReactNode } from 'react';

export interface OptionGroupOption {
    value: string;
    title: string;
    subtitle?: string;
    emoji?: string;
    icon?: ReactNode;
    disabled?: boolean;
    badge?: { label: string; tooltip: string };
}

interface OptionGroupFieldProps {
    name: string;
    options: readonly OptionGroupOption[];
    multiple?: boolean;
    columns?: 'single' | 'auto';
    onChange?: (value: string) => void;
}

export const OptionGroupField = (props: OptionGroupFieldProps) => {
    const [field, meta] = useField<string | string[] | undefined>({ name: props.name });

    const isChecked = (optionValue: string) => {
        if (props.multiple) {
            return Array.isArray(field.value) && field.value.includes(optionValue);
        }

        return field.value === optionValue;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        field.onChange(event);
        props.onChange?.(event.target.value);
    };

    return (
        <FormControl error={meta.touched && !!meta.error} fullWidth>
            <OptionList columns={props.columns}>
                {props.options.map((option) => (
                    <OptionCard
                        key={option.value}
                        name={field.name}
                        value={option.value}
                        multiple={props.multiple}
                        checked={isChecked(option.value)}
                        disabled={option.disabled}
                        badge={option.badge}
                        onChange={handleChange}
                        onBlur={field.onBlur}
                        title={option.title}
                        subtitle={option.subtitle}
                        emoji={option.emoji}
                        icon={option.icon}
                    />
                ))}
            </OptionList>
            <CustomErrorMessage meta={meta} />
        </FormControl>
    );
};
